import React, { useEffect, useRef, useState } from "react";

import { Grid, Box, Modal } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import { NotificationToast } from "../../../components/Generic/Toast/NotificationToast";
import { PublicFooter } from "../../../components/PublicFooter";
import AuthLeftPanel from "../../../components/AuthLeftPanel";
import { PUBLIC_ROUTES, USER_TOKEN } from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import { changeTitlePageByResource } from "../../../helpers/helpers";
import { Error } from "../../../models/error";
import UserService from "../../../services/user.service";
import { CodeError } from "../../../util/enums";
import UpdatePasswordForm, { UpdatePasswordFormHandle } from "./components/Form";
import { UpdatedPassword } from "./components/UpdatedScreen";
import useStyles from "./styles";

interface HistoryState {
  token: string;
  user: {
    username?: string;
    email: string;
    id: string;
  };
}

const UpdatePassword = () => {
  const history = useHistory<HistoryState>();
  const location = useLocation<HistoryState>();
  const { currentUser, userLogout } = useAppState();

  const updatePasswordFormRef = useRef<UpdatePasswordFormHandle>(null);

  const [userId, setUserId] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string>("");
  const [passwordUpdated, setPasswordUpdated] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>("");
  const [passwordComplexity, setPasswordComplexity] = useState<boolean>(false);
  const [isAlreadyLogin, setIsAlreadyLogin] = useState<boolean>(false);

  const classes = useStyles();

  const { t: translate } = useTranslation();

  const { mutate: recoverPassword, isLoading } = useMutation(
    ["users.recover.password"],
    (password: string) => {
      const identifier = userId || userEmail;

      return isAlreadyLogin
        ? UserService.initPassword(identifier, password, password)
        : UserService.recoverPassword(identifier, password, password);
    },
    {
      onSuccess: (response) => {
        if (response && response.data) {
          setPasswordUpdated(true);
          setTimeout(() => {
            if (currentUser) userLogout();
            history.replace({ pathname: PUBLIC_ROUTES.SIGN_IN });
          }, 5000);
        }
      },
      onError: (error: AxiosError<Error>) => {
        const errorObject = error?.response?.data;

        if (
          errorObject?.error === CodeError.THIS_PASSWORD_HAS_ALREADY_BEEN_USED &&
          errorObject.message
        ) {
          setFormError(errorObject?.message);
        }
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const isDevMode = window.location.search.includes("dev=true");

  useQuery(
    ["users.company.security-config"],
    () => {
      if (!localStorage?.getItem(USER_TOKEN)) {
        localStorage.setItem(USER_TOKEN, `Bearer ${location?.state?.token || ""}`);
      }
      return UserService.checkPasswordComplexity();
    },
    {
      enabled: !!userId && !isDevMode, // Don't run query in dev mode
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        setPasswordComplexity(
          (response && response.data)
            ? response.data?.passwordComplexity
            : true
        );
        localStorage.removeItem(USER_TOKEN);
      },
      onError: (error) => {
        localStorage.removeItem(USER_TOKEN);
      },
    }
  );

  useEffect(() => {
    setIsAlreadyLogin(!!localStorage?.getItem(USER_TOKEN));
  }, [])

  useEffect(() => {
    changeTitlePageByResource(translate("recoverPassword"));
    // Allow direct access for development/visualization purposes
    const isDevMode = window.location.search.includes("dev=true");

    if (isDevMode) {
      // Set a dummy userId for development viewing and enable password complexity
      setUserId("dev-user-id");
      setPasswordComplexity(true);
      return;
    }

    if (!currentUser) {
      if (location.state && location.state.user) {
        const { user } = location.state;

        setUserId(String(user.id));
        setUserEmail(user.email || user.username || '');
      } else {
        history.replace(PUBLIC_ROUTES.FORGOT);
      }
    } else if (currentUser.id) {
      setUserId(String(currentUser.id));
      if (currentUser?.email || currentUser?.username) {
        setUserEmail(currentUser.email || currentUser.username || '');
      }
    }
  }, [currentUser, history, location.state]);

  const clearFormErrors = () => {
    setFormError("");
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!userId && !userEmail) {
      history.replace(PUBLIC_ROUTES.FORGOT);

      return;
    }

    recoverPassword(updatePasswordFormRef?.current?.password ?? "");
  };

  const [rightSideLoaded, setRightSideLoaded] = useState(false);

  useEffect(() => {
    // Left side loads first, then right side after delay
    const timer = setTimeout(() => {
      setRightSideLoaded(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const renderContent = () => {
    return (
      <div className={classes.mainDiv}>
        <Grid className={classes.container} container direction="row">
          <Grid item xs={12} sm={6}>
            <Box className={classes.leftBox}>
              <AuthLeftPanel showContentDelay={0} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
          <Box
            className={classes.rightBox}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              opacity: rightSideLoaded ? 1 : 0,
              transition: "opacity 0.6s ease-in-out",
              position: "relative",
            }}
          >
            <Box sx={{ width: "100%", maxWidth: "520px", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box sx={{ width: "100%", display: "flex", alignItems: "flex-start", justifyContent: "flex-start" }}>
                {passwordUpdated ? (
                  <UpdatedPassword />
                ) : (
                  <UpdatePasswordForm
                    ref={updatePasswordFormRef}
                    handleSubmit={handleSubmit}
                    passwordComplexity={passwordComplexity}
                    isLoading={isLoading}
                    formError={formError}
                    clearFormErrors={clearFormErrors}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ width: "100%", maxWidth: "520px", pb: 2, display: "flex", justifyContent: "center" }}>
              <PublicFooter />
            </Box>
          </Box>
          </Grid>
        </Grid>
      </div>
    );
  };

  if (currentUser) return <Modal open={true}>{renderContent()}</Modal>;

  return renderContent();
};

export default UpdatePassword;
